<template>
  <div>
    <div v-if="loading" class="d-ta-center d-py8">
      <dt-skeleton width="100%" height="16" class="d-mb4" />
      <dt-skeleton width="80%" height="16" class="d-mb4" />
      <dt-skeleton width="60%" height="16" />
    </div>
    <div v-else-if="messages.length === 0" class="d-d-flex d-fd-column">
      <!-- Message Composer for empty conversations -->
      <div class="dp3-conversation-preview-composer d-p16">
        <!-- Action Buttons (shown by default) -->
        <DtRecipeMessageInput
          v-model="messageText"
          placeholder="Quick reply..."
          :richText="false"
          inputClass="d-hmx24"
          inputAriaLabel="Quick reply message"
          @submit="handleMessageSubmit"
          :showCancel="hasContent"
          :showSend="hasContent"
          :disableSend="!hasContent"
          :showCharacterLimit="false"
          :showEmojiPicker="false"
          :showImagePicker="false"
          @cancel="hideQuickReply"
        />
      </div>
    </div>
    <div v-else class="d-d-flex d-fd-column">
      <div class="d-d-flex d-fd-column d-of-y-auto d-hmx264 d-pt8">
        <DtRecipeFeedItemRow
          v-for="(message, index) in messages"
          :key="message.timestamp + '_' + (message.senderId || message.sender || 'system')"
          :class="['dp3-conversation-preview-item', { 'd-pb4': index < messages.length - 1 }]"
          :show-header="true"
          :displayName="getSenderName(message)"
          :time="formatMessageTime(message.timestamp)"
          :is-active="false"
        >
          <template #avatar>
            <ContactAvatar v-bind="getSenderAvatarData(message).props" avatarClass="d-as-flex-start dp3-avatar__md" />
          </template>
          <div class="d-fs-200 d-lh-copy d-fc-secondary dp3-preview-message-text">
            {{ message.text || message.message || 'No message content' }}
          </div>
        </DtRecipeFeedItemRow>
      </div>

      <!-- Message Composer -->
      <div class="dp3-conversation-preview-composer d-p16 d-pb0 d-bt d-bc-subtle">
        <!-- Show buttons only if there are unread messages -->
        <div v-if="!showMessageInput && props.item.unread_count > 0">
          <!-- Action Buttons (shown by default for unread items) -->
          <div class="d-d-flex d-ai-center d-jc-space-between d-mb16 d-g8">
            <dt-button class="d-w100p" size="md" importance="outlined" kind="muted" @click="showQuickReply">
              Quick Reply
            </dt-button>
            <dt-button class="d-w100p" v-if="!isMarkedAsRead" size="md" importance="outlined" @click="handleMarkAsRead"
              >Mark as Read</dt-button
            >
          </div>
        </div>

        <!-- Message Input (always shown for read items, or when Quick Reply is clicked) -->
        <div
          class="d-pb16"
          v-if="showMessageInput || props.item.unread_count === 0 || props.item.unread_count === undefined"
        >
          <DtRecipeMessageInput
            v-model="messageText"
            placeholder="Quick reply..."
            :richText="false"
            inputAriaLabel="Quick reply message"
            @submit="handleMessageSubmit"
            inputClass="d-p0 d-pt4 d-m0"
            class="d-pt4 d-px8 d-pb0"
            :showCancel="hasContent"
            :showSend="hasContent"
            :disableSend="!hasContent"
            :showCharacterLimit="false"
            :showEmojiPicker="false"
            :showImagePicker="false"
            @cancel="hideQuickReply"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, nextTick } from 'vue';
import { useDataStore } from '@/store/useDataStore';
import { useUserStore } from '@/store/useUserStore';
import { useTimestampFormat } from '@/composables/formatting/useTimestampFormat';
import { useAvatarUtilities } from '@/composables/ui/useAvatarUtilities';
import { DtSkeleton, DtButton, DtRecipeFeedItemRow, DtIcon, DtRecipeMessageInput } from '@dialpad/dialtone/vue3';
import ContactAvatar from '@/components/ContactAvatar.vue';
import { DtIconQuickReply } from '@dialpad/dialtone-icons/vue3';
import { useDebugLogger } from '@/utils/debugLogger';

const debugLogger = useDebugLogger();

const props = defineProps<{
  item: any;
}>();

const emit = defineEmits<{
  'close-hovercard': [];
}>();

const dataStore = useDataStore();
const userStore = useUserStore();
const { formatTime } = useTimestampFormat();
const { findContactById, createAvatarPropsFromContact } = useAvatarUtilities(userStore.userId);

const loading = ref(false);
const messages = ref([]);
const messageText = ref('');
const showMessageInput = ref(false);
const isMarkedAsRead = ref(false);

// Computed property to check if there's content in the input
const hasContent = computed(() => {
  const value = messageText.value;

  // Handle string values
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }

  // Handle rich text object values
  if (typeof value === 'object' && value !== null) {
    // For rich text objects, check if there's any text content
    try {
      const textContent = extractTextFromRichText(value);
      return textContent.trim().length > 0;
    } catch (e) {
      return false;
    }
  }

  // Default case - no content
  return false;
});

// Helper function to extract text from rich text content
const extractTextFromRichText = (richTextContent: any): string => {
  if (!richTextContent || !richTextContent.content) return '';

  let text = '';
  const traverse = (node: any) => {
    if (node.type === 'text') {
      text += node.text || '';
    } else if (node.content && Array.isArray(node.content)) {
      node.content.forEach(traverse);
    }
  };

  if (Array.isArray(richTextContent.content)) {
    richTextContent.content.forEach(traverse);
  }

  return text;
};

// Create a conversation object for the MessageComposer (memoized to reduce re-renders)
const conversationObject = computed(() => {
  if (!props.item) return null;

  // Use the item itself as the base, avoiding unnecessary data store lookups
  const baseObject = {
    id: props.item.id,
    name: props.item.name,
    itemType: props.item.itemType,
  };

  // Only add participants for group messages
  if (props.item.itemType === 'group_message' && props.item.participants) {
    baseObject.participants = props.item.participants;
  }

  return baseObject;
});

// Helper to create a new message object
const createMessage = ({ text, userId, replyTo, replyMessageText, replyName, senderId }) => {
  return {
    id: Math.random().toString(36).slice(2),
    senderId: senderId || userId,
    text,
    timestamp: new Date().toISOString(),
    ...(replyTo ? { replyTo } : {}),
    ...(replyMessageText ? { replyMessageText } : {}),
    ...(replyName ? { replyName } : {}),
  };
};

// Handle message submission
const handleMessageSubmit = (submitData?: any) => {
  debugLogger.log('🔥 handleMessageSubmit called:', {
    submitData,
    messageText: messageText.value,
    item: props.item,
  });

  // Extract text from different possible formats
  let text = '';

  if (typeof submitData === 'string') {
    text = submitData;
  } else if (submitData && typeof submitData === 'object') {
    // Handle rich text object or event object
    text = submitData.text || extractTextFromRichText(submitData) || '';
  } else {
    // Fallback to messageText value
    text = typeof messageText.value === 'string' ? messageText.value : extractTextFromRichText(messageText.value);
  }

  debugLogger.log('📝 Extracted text:', text);

  if (!text.trim()) {
    debugLogger.log('❌ No text to submit');
    return;
  }

  // Clear the input
  messageText.value = '';

  // Add message logic here (same as before)
  const currentUserId = userStore.userId;
  debugLogger.log('👤 Current user ID:', currentUserId);

  const newMessage = {
    id: Date.now().toString(),
    text: text.trim(),
    senderId: currentUserId,
    sender: currentUserId,
    timestamp: new Date().toISOString(),
    type: 'text',
  };

  debugLogger.log('💬 New message created:', newMessage);
  debugLogger.log('📂 Item type:', props.item.itemType, 'Item ID:', props.item.id);

  // Add to appropriate data store based on item type
  if (props.item.itemType === 'contact') {
    debugLogger.log('📞 Adding to contact messages');
    // Add to direct messages
    if (!dataStore.userContactsMessagesData[currentUserId]) {
      dataStore.userContactsMessagesData[currentUserId] = [];
    }

    let thread = dataStore.userContactsMessagesData[currentUserId].find(c => c.contact_id === props.item.id);
    if (!thread) {
      debugLogger.log('🆕 Creating new contact thread');
      thread = {
        contact_id: props.item.id,
        messages: [],
      };
      dataStore.userContactsMessagesData[currentUserId].push(thread);
    }

    thread.messages.push(newMessage);
    debugLogger.log('✅ Added to contact thread:', thread);
  } else if (props.item.itemType === 'group_message') {
    debugLogger.log('👥 Adding to group messages');
    // Add to group messages
    const group = dataStore.groupMessagesData?.group_messages?.find(g => g.id === props.item.id);
    debugLogger.log('🔍 Found group:', group);

    if (group) {
      let storageKey = group.url_id || group.slug || group.group_id;
      debugLogger.log('🔑 Storage key:', storageKey);

      if (!dataStore.groupMessagesConversationsData[storageKey]) {
        dataStore.groupMessagesConversationsData[storageKey] = [];
      }

      dataStore.groupMessagesConversationsData[storageKey].push(newMessage);
      debugLogger.log('✅ Added to group messages:', dataStore.groupMessagesConversationsData[storageKey]);
    } else {
      debugLogger.log('❌ Group not found for ID:', props.item.id);
    }
  } else if (props.item.itemType === 'channel') {
    debugLogger.log('📺 Adding to channel messages');
    // Add to channel messages using userChannelMessagesData structure
    if (!dataStore.userChannelMessagesData[currentUserId]) {
      dataStore.userChannelMessagesData[currentUserId] = [];
    }

    let thread = dataStore.userChannelMessagesData[currentUserId].find(m => m.link_id === props.item.id);
    if (!thread) {
      debugLogger.log('🆕 Creating new channel thread');
      thread = {
        link_id: props.item.id,
        messages: [],
      };
      dataStore.userChannelMessagesData[currentUserId].push(thread);
    }

    thread.messages.push(newMessage);
    debugLogger.log('✅ Added to channel thread:', thread);
  } else {
    debugLogger.log('❓ Unknown item type:', props.item.itemType);
  }

  // Refresh the messages display
  debugLogger.log('🔄 Refreshing messages...');
  refreshMessages();

  // Mark the conversation as read after sending a message
  debugLogger.log('📖 Marking conversation as read after sending message...');
  isMarkedAsRead.value = true;

  if (props.item && currentUserId) {
    // Clear unread count using the dataStore method
    dataStore.clearUnreadCount(props.item.id, props.item.itemType, currentUserId);

    // For group messages, also update the group in groupMessagesData
    if (props.item.itemType === 'group_message') {
      const group = dataStore.groupMessagesData?.group_messages?.find(g => g.id === props.item.id);
      if (group) {
        group.unread_count = 0;
        group.has_unread = false;
      }
    }
  }
};

// Refresh messages after adding a new one
const refreshMessages = () => {
  const allMessages = getConversationMessages(props.item);
  messages.value = allMessages.slice(-3);
};

// Get conversation messages based on item type
const getConversationMessages = (item: any) => {
  const currentUserId = userStore.userId;

  try {
    if (item.itemType === 'contact') {
      // Direct messages
      const userContactMessages = dataStore.userContactsMessagesData[currentUserId] || [];
      const thread = userContactMessages.find(c => c.contact_id === item.id);
      return thread?.messages || [];
    }

    if (item.itemType === 'group_message') {
      // Group messages - try multiple approaches to find the conversation
      let groupMessages = [];

      // First try with the item ID directly
      groupMessages = dataStore.groupMessagesConversationsData?.[item.id] || [];

      // If not found, try with url_id or slug
      if (groupMessages.length === 0) {
        const group = dataStore.groupMessagesData?.group_messages?.find(g => g.id === item.id);
        if (group) {
          const allKeys = Object.keys(dataStore.groupMessagesConversationsData || {});
          const matchingKey = allKeys.find(key => key === group.url_id || key === group.slug || key === group.group_id);
          if (matchingKey) {
            groupMessages = dataStore.groupMessagesConversationsData[matchingKey] || [];
          }
        }
      }

      return groupMessages;
    }

    if (item.itemType === 'channel') {
      // Channel messages
      const sharedMessages = dataStore.sharedChannelMessagesData?.find(m => m.link_id === item.id)?.messages || [];
      const personalMessages =
        dataStore.userChannelMessagesData?.[currentUserId]?.find(m => m.link_id === item.id)?.messages || [];

      // Combine and sort by timestamp
      const combinedMessages = [...sharedMessages, ...personalMessages].sort(
        (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      );

      return combinedMessages;
    }

    return [];
  } catch (error) {
    return [];
  }
};

// Helper to get sender ID from message (handles both sender_id and senderId properties)
const getMessageSenderId = (message: any) => {
  return message.senderId || message.sender_id || message.sender;
};

// Get sender avatar data using composable
const getSenderAvatarData = (message: any) => {
  const senderId = getMessageSenderId(message);
  if (!senderId) {
    return {
      props: {
        fullName: 'Unknown',
        imageAlt: 'Unknown',
        imageSrc: null,
        avatarColor: null,
        id: 'unknown',
        size: 'md',
      },
    };
  }

  // If sender is current user, handle differently
  if (senderId === userStore.userId) {
    const currentUserContact = findContactById(senderId);
    if (currentUserContact) {
      const avatarProps = createAvatarPropsFromContact(currentUserContact, { size: 'md' });
      return { props: avatarProps };
    }
    return {
      props: {
        fullName: userStore.name || 'You',
        imageAlt: userStore.name || 'You',
        imageSrc: null,
        avatarColor: null,
        id: senderId,
        size: 'md',
      },
    };
  }

  // Find sender using composable
  const contact = findContactById(senderId);
  if (contact) {
    const avatarProps = createAvatarPropsFromContact(contact, { size: 'md' });
    return { props: avatarProps };
  }

  return {
    props: {
      fullName: 'Unknown',
      imageAlt: 'Unknown',
      imageSrc: null,
      avatarColor: null,
      id: senderId,
      size: 'md',
    },
  };
};

// Helper functions for backward compatibility
const getSenderName = (message: any) => {
  return getSenderAvatarData(message).props.fullName;
};

const getSenderAvatar = (message: any) => {
  return getSenderAvatarData(message).props.imageSrc;
};

const getSenderAvatarColor = (message: any) => {
  return getSenderAvatarData(message).props.avatarColor;
};

// Format message timestamp
const formatMessageTime = (timestamp: string) => {
  return formatTime(timestamp);
};

// Load messages when item changes
watch(
  () => props.item,
  newItem => {
    if (!newItem) {
      messages.value = [];
      return;
    }

    loading.value = true;

    // Get messages immediately without setTimeout to reduce blinking
    nextTick(() => {
      const allMessages = getConversationMessages(newItem);
      // Get the last 3 messages
      messages.value = allMessages.slice(-3);
      loading.value = false;
    });
  },
  { immediate: true }
);

const handleMarkAsRead = () => {
  isMarkedAsRead.value = true;
  // Use the dataStore's clearUnreadCount method for proper reactivity
  const currentUserId = userStore.userId;

  if (props.item && currentUserId) {
    // Clear unread count using the dataStore method
    dataStore.clearUnreadCount(props.item.id, props.item.itemType, currentUserId);

    // For group messages, also update the group in groupMessagesData
    if (props.item.itemType === 'group_message') {
      const group = dataStore.groupMessagesData?.group_messages?.find(g => g.id === props.item.id);
      if (group) {
        group.unread_count = 0;
        group.has_unread = false;
      }
    }
  }
  emit('close-hovercard');
};

const showQuickReply = () => {
  showMessageInput.value = true;
};

const hideQuickReply = () => {
  showMessageInput.value = false;
};
</script>

<style lang="less"></style>
